import { ComponentProps, useCallback, useMemo, useRef, useState } from 'react';

import { Popover } from '@/components/atoms/Popover';
import { Dialog } from '@/components/atoms/Dialog';

import { Input } from '@/components/atoms/Input';
import { Chevron, HorizontalDots, Trash } from '@/resources/assets/images';
import { format } from 'date-fns';
import { useVirtualElements } from '@/hooks/useVirtualElements';
import { Table } from '@/components/atoms/Table';
import {
	type SortingState,
	useTable,
	flexRender,
	createColumnHelper
} from '@/hooks/useTable';

import type { List } from '@/modules/dashboard/dashboardTypes';
import { Select } from '@/components/atoms/Select';
import type { RowSelectionState } from '@tanstack/react-table';
import { cn } from '@/lib/classNames';
import { useHistory } from 'react-router-dom';
import { sendAnalytics } from '@/services/analyticsService';
import { useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { Tooltip } from '@/components/atoms/Tooltip';
import { useToast } from '@/components/atoms/Toast';
import { useDuplicateListMutation } from '../../hooks/useDuplicateListMutation';
import { useRemoveListMutation } from '../../hooks/useRemoveListMutation';
import { useEditListNameMutation } from '../../hooks/useEditListNameMutation';
import { useRemoveBatchListMutation } from '../../hooks/useRemoveBatchListMutation';
import { generateNewCopyName } from '../../utils/generateNewCopyName';

export type ListsTableProps = {
	listData: Array<List>;
	pageListData: Array<List>;
	currentPage: number;
	sorting: SortingState;
};

export const ListsTable = ({
	listData,
	pageListData,
	sorting,
	currentPage
}: ListsTableProps) => {
	const history = useHistory();

	const [selectedElements, setSelectedElements] = useState<RowSelectionState>(
		{}
	);
	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);

	const handleRowClick = useCallback(
		({ id }: Pick<List, 'id'>, event?: React.MouseEvent<HTMLElement>) => {
			event?.stopPropagation();

			const clickedElement = event?.target as HTMLElement | null;
			const tagName = clickedElement?.tagName;

			const isClickAllowed =
				tagName === 'TD' ||
				clickedElement?.closest('[data-activate-row-click]');

			if (isClickAllowed) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					properties: {
						listId: id
					},
					eventName: 'User clicked a list'
				});

				history.push(`/lists/details/${id}`);
			}
		},
		[history, user.email]
	);

	const columns = useMemo(() => {
		const columnHelper =
			createColumnHelper<ListsTableProps['pageListData'][number]>();

		return [
			columnHelper.accessor('id', {
				header: ({ table }) => {
					const selectionMap = {
						all: listData,
						page: pageListData
					};

					const handleSelectElements = (option: string) => {
						const list =
							selectionMap[option === 'all' ? 'all' : 'page'];

						const listIds = list.map(element => element.id);

						const listIdsInSelectionState =
							listIds.reduce<RowSelectionState>((acc, item) => {
								acc[item] = true;
								return acc;
							}, {});

						setSelectedElements(state => ({
							...state,
							...listIdsInSelectionState
						}));

						sendAnalytics({
							type: 'track',
							userId: user.email,
							properties: {
								selectionType:
									option === 'all' ? 'all' : 'page',
								listCount: list.length
							},
							eventName: 'User selected multiple list elements'
						});
					};

					return (
						<Select.Root
							value=""
							onValueChange={handleSelectElements}
						>
							<Select.Trigger className="border-none bg-transparent p-1 transition-colors hover:bg-dm-black4 data-[state=open]:bg-dm-black3">
								<Input
									type="checkbox"
									readOnly
									className="[&>[type=checkbox]+span_.checkbox-icon_path]:fill-dm-gold"
									{...{
										checked: table.getIsAllRowsSelected()
									}}
								/>
							</Select.Trigger>

							<Select.Content className="p-1">
								<Select.Item value="all">
									Select all
								</Select.Item>
								<Select.Item value={`page-${currentPage}`}>
									Select page
								</Select.Item>
							</Select.Content>
						</Select.Root>
					);
				},
				cell: ({ row }) => (
					<Input
						type="checkbox"
						{...{
							checked: row.getIsSelected(),
							disabled: !row.getCanSelect(),
							indeterminate: row.getIsSomeSelected(),
							onChange: row.getToggleSelectedHandler()
						}}
					/>
				),
				size: 80
			}),
			columnHelper.accessor('name', {
				header: () => (
					<span className="font-barlow text-sm font-bold">
						List Name
					</span>
				),
				cell: ({ getValue }) => (
					<Tooltip.Provider>
						<Tooltip.Root>
							<Tooltip.Trigger
								data-activate-row-click
								className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-start font-barlow font-semibold"
							>
								{getValue()}
							</Tooltip.Trigger>

							<Tooltip.Content>{getValue()}</Tooltip.Content>
						</Tooltip.Root>
					</Tooltip.Provider>
				),
				size: 240
			}),
			columnHelper.accessor('businessIds', {
				header: () => (
					<span className="font-barlow text-sm font-bold">
						Businesses Count
					</span>
				),
				cell: ({ getValue }) => (
					<span className="pointer-events-none font-barlow text-sm">
						{getValue()?.length}
					</span>
				),
				size: 180
			}),
			columnHelper.accessor('updatedAt', {
				header: () => (
					<span className="font-barlow text-sm font-bold">
						Last Update
					</span>
				),
				cell: ({ getValue }) => (
					<span className="pointer-events-none font-barlow text-sm">
						{format(new Date(getValue()), 'MMM d, y')}
					</span>
				)
			}),
			columnHelper.accessor('userId', {
				header: '',
				cell: ({ row }) => (
					<Popover.Root>
						<Popover.Trigger className="group aspect-square h-8 w-8 rounded-md border-transparent p-1.5 transition-colors hover:bg-dm-gray focus:ring-0 data-[state=open]:bg-dm-black3">
							<HorizontalDots className="rotate-90 transition-colors group-hover:[&_path]:fill-orange group-data-[state=open]:[&_path]:fill-orange" />
						</Popover.Trigger>

						<Popover.Portal>
							<Popover.Content className="flex flex-col items-start gap-2">
								<DuplicateListModal {...row.original} />

								<EditNameModal {...row.original} />

								<RemoveListModal {...row.original} />
							</Popover.Content>
						</Popover.Portal>
					</Popover.Root>
				),
				size: 80
			})
		];
	}, [currentPage, listData, pageListData, user.email]);

	const { rows, headers } = useTable({
		data: pageListData,
		columns,
		getRowId: row => row.id,
		defaultColumn: {
			size: 0,
			minSize: 0,
			maxSize: Number.MAX_SAFE_INTEGER
		},
		state: {
			sorting,
			rowSelection: selectedElements
		},
		onRowSelectionChange: setSelectedElements
	});

	const { containerRef, virtualRows, paddingTop, paddingBottom } =
		useVirtualElements<HTMLDivElement>({
			count: rows.length ?? 0,
			estimateSize: () => 40,
			overscan: 15,
			scrollMargin: 150
		});

	const handleDeselectElements = () => {
		setSelectedElements({});
	};

	const selectedElementsCount = Object.entries(selectedElements).length;
	const isSelectedHeaderVisible = selectedElementsCount > 0;

	return (
		<div
			data-is-selected-header-visible={isSelectedHeaderVisible}
			className="relative h-full transition-all data-[is-selected-header-visible=true]:pt-14"
		>
			<SelectedMenuHeader
				className={cn(
					'h-14 -translate-y-[5px] opacity-0 duration-200 ease-in-out',
					'data-[is-selected-header-visible=true]:translate-y-0 data-[is-selected-header-visible=true]:opacity-100 data-[is-selected-header-visible=true]:delay-150'
				)}
				data-is-selected-header-visible={isSelectedHeaderVisible}
			>
				<section className="flex items-center gap-2">
					<button
						type="button"
						onClick={() => {
							handleDeselectElements();

							sendAnalytics({
								type: 'track',
								userId: user.email,
								eventName: 'User deselected all list elements'
							});
						}}
					>
						<Input
							type="checkbox"
							className="aspect-square h-5 !w-5 bg-dm-white p-0 [&_path]:fill-dm-smart-blue"
							rounded="md"
							readOnly
							checked={selectedElementsCount === listData.length}
							indeterminate={
								selectedElementsCount > 0 &&
								selectedElementsCount < listData.length
							}
						/>
					</button>

					<p className="text-sm font-semibold text-orange">
						{selectedElementsCount} selected
					</p>
				</section>

				<MoreActionsHeadersMenu
					selectedElements={selectedElements}
					selectedElementsCount={selectedElementsCount}
					onDeleteElements={handleDeselectElements}
				/>
			</SelectedMenuHeader>

			<Table.Root
				ref={containerRef}
				className="relative z-10 min-h-[50dvh]"
			>
				<Table.Header>
					{headers.map(headerGroup => (
						<Table.Row key={headerGroup.id} className="bg-dm-black">
							{headerGroup.headers.map(header => (
								<Table.Head
									key={header.id}
									style={{
										width: header.getSize() || '100%'
									}}
								>
									<span className="inline-flex items-center gap-2 fill-dm-white">
										{flexRender(
											header.column.columnDef.header,
											header.getContext()
										)}

										{header.column.getIsSorted() ? (
											<Chevron
												data-is-asc={
													header.column.getIsSorted() !==
													'desc'
												}
												className="transition-transform data-[is-asc=true]:rotate-180"
											/>
										) : null}
									</span>
								</Table.Head>
							))}
						</Table.Row>
					))}
				</Table.Header>

				<Table.Body
					columns={columns.length}
					paddingTop={paddingTop}
					paddingBottom={paddingBottom}
				>
					{virtualRows?.length ? (
						<>
							{virtualRows.map(({ index }) => {
								const row = rows[index];

								if (!row) {
									return null;
								}

								const handleTableRowClick = (
									event: React.MouseEvent<HTMLElement>
								) => {
									handleRowClick(
										{
											id: row.id
										},
										event
									);
								};

								return (
									<Table.Row
										key={row.id}
										onClick={handleTableRowClick}
										className="!cursor-auto lg:!cursor-pointer" // block cursor on mobile
									>
										{row.getVisibleCells().map(cell => {
											return (
												<Table.Cell
													key={cell.id}
													style={{
														width:
															cell.column.getSize() ||
															'100%'
													}}
												>
													{flexRender(
														cell.column.columnDef
															.cell,
														cell.getContext()
													)}
												</Table.Cell>
											);
										})}
									</Table.Row>
								);
							})}
						</>
					) : (
						<Table.Row>
							<Table.Cell
								colSpan={columns.length}
								className="h-24 text-center"
							>
								<div className="m-6 flex flex-col items-start sm:items-center">
									No Results
								</div>
							</Table.Cell>
						</Table.Row>
					)}
				</Table.Body>
			</Table.Root>
		</div>
	);
};

type ModalProps = ListsTableProps['pageListData'][number];

const DuplicateListModal = ({ name, ...list }: ModalProps) => {
	const { mutate } = useDuplicateListMutation();

	const copyName = generateNewCopyName(name);

	const handleDuplicateList = useCallback(() => {
		mutate({
			...list,
			name: copyName
		});
	}, [copyName, list, mutate]);

	return (
		<Dialog.Root>
			<Dialog.Trigger className="h-10 w-full rounded-md px-3 text-start transition-colors hover:bg-dm-black3.5">
				Duplicate
			</Dialog.Trigger>

			<Dialog.Content className="gap-8 border-none">
				<Dialog.Title>
					Duplicate List{' '}
					<span className="text-orange-dark">{name}</span>
				</Dialog.Title>

				<Dialog.Description>
					The selected list will be duplicated with new name{' '}
					<span className="text-orange-dark">{copyName}</span>
				</Dialog.Description>

				<Dialog.Footer>
					<Dialog.Close className="font-barlow font-semibold text-orange hover:underline">
						Cancel
					</Dialog.Close>

					<Dialog.Close asChild>
						<button
							type="button"
							onClick={handleDuplicateList}
							className="inline-flex items-center rounded-md border border-orange px-3 py-1.5 font-semibold text-orange transition-colors hover:bg-orange hover:text-dm-black"
						>
							Confirm duplication
						</button>
					</Dialog.Close>
				</Dialog.Footer>
			</Dialog.Content>
		</Dialog.Root>
	);
};

const EditNameModal = ({ name, ...list }: ModalProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const { toast } = useToast();
	const { mutate } = useEditListNameMutation();

	const handleEditListName = useCallback(() => {
		if (inputRef.current?.value === name) {
			setIsOpen(false);
			return;
		}

		const trimmedName = inputRef.current?.value.trim();

		if (!trimmedName) {
			toast({
				title: 'List name cannot be empty',
				variant: 'error'
			});
			return;
		}

		mutate({
			...list,
			name: trimmedName
		});
		setIsOpen(false);
	}, [list, mutate, name, toast]);

	return (
		<Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
			<Dialog.Trigger className="h-10 w-full rounded-md px-3 text-start transition-colors hover:bg-dm-black3.5">
				Edit name
			</Dialog.Trigger>

			<Dialog.Content className="gap-8 border-none">
				<Dialog.Title>Edit List Name</Dialog.Title>

				<Dialog.Description>
					<Input
						defaultValue={name}
						size="lg"
						padding="md"
						rounded="md"
						className="bg-dm-white [&_input]:!text-dm-black"
						ref={inputRef}
					/>
				</Dialog.Description>

				<Dialog.Footer>
					<Dialog.Close className="font-barlow font-semibold text-orange hover:underline">
						Cancel
					</Dialog.Close>

					<button
						type="button"
						onClick={handleEditListName}
						className="inline-flex items-center rounded-md border border-orange px-3 py-1.5 font-semibold text-orange transition-colors hover:bg-orange hover:text-dm-black"
					>
						Confirm new name
					</button>
				</Dialog.Footer>
			</Dialog.Content>
		</Dialog.Root>
	);
};

const RemoveListModal = ({ ...list }: ModalProps) => {
	const { name } = list;

	const { mutate } = useRemoveListMutation();

	const handleRemoveList = useCallback(() => {
		mutate({
			...list
		});
	}, [list, mutate]);

	return (
		<Dialog.Root>
			<Dialog.Trigger className="h-10 w-full rounded-md px-3 text-start transition-colors hover:bg-dm-black3.5">
				Remove
			</Dialog.Trigger>

			<Dialog.Content className="gap-8 border-none">
				<Dialog.Title>Are you sure?</Dialog.Title>

				<Dialog.Description>
					List <span className="text-orange">{name}</span> will be
					permanently removed.
				</Dialog.Description>

				<Dialog.Footer>
					<Dialog.Close className="font-barlow font-semibold text-orange hover:underline">
						Cancel
					</Dialog.Close>

					<Dialog.Close asChild>
						<button
							type="button"
							onClick={handleRemoveList}
							className="inline-flex items-center gap-1 rounded-md border border-dm-red px-3 py-1.5 font-semibold text-dm-red transition-colors hover:border-red hover:bg-red hover:text-dm-white"
						>
							<Trash />
							Confirm Deletion
						</button>
					</Dialog.Close>
				</Dialog.Footer>
			</Dialog.Content>
		</Dialog.Root>
	);
};

const SelectedMenuHeader = ({ className, ...props }: ComponentProps<'div'>) => {
	return (
		<div
			{...props}
			className={cn(
				'absolute left-0 top-0 flex w-full items-center justify-start gap-6 rounded-t-lg border-b border-dm-black3.5 bg-dm-black1 p-4',
				className
			)}
		/>
	);
};

type MoreActionsHeadersMenuProps = {
	selectedElements: RowSelectionState;
	selectedElementsCount: number;
	onDeleteElements: VoidFunction;
};

const MoreActionsHeadersMenu = ({
	selectedElements,
	selectedElementsCount,
	onDeleteElements
}: MoreActionsHeadersMenuProps) => {
	const { mutate } = useRemoveBatchListMutation();

	const [isOpen, setIsOpen] = useState(false);

	const handleBatchDelete = () => {
		mutate(Object.keys(selectedElements));
		setIsOpen(false);

		onDeleteElements();
	};

	return (
		<Popover.Root open={isOpen} onOpenChange={setIsOpen}>
			<Popover.Trigger className="w-fit gap-1 text-dm-white">
				More actions
				<Chevron className="[&_path]:fill-dm-white" />
			</Popover.Trigger>

			<Dialog.Root>
				<Popover.Portal>
					<Popover.Content
						className="z-50"
						sideOffset={5}
						align="start"
					>
						<Dialog.Trigger
							asChild
							className="h-10 w-full rounded-md px-3 text-start transition-colors hover:bg-dm-black3.5"
						>
							<Popover.Button>
								Delete {selectedElementsCount} lists
							</Popover.Button>
						</Dialog.Trigger>
					</Popover.Content>
				</Popover.Portal>

				<Dialog.Content className="gap-8 border-none">
					<Dialog.Title>Are you sure?</Dialog.Title>

					<Dialog.Description>
						The selected lists will be permanently removed.
					</Dialog.Description>

					<Dialog.Footer>
						<Dialog.Close className="font-barlow font-semibold text-orange hover:underline">
							Cancel
						</Dialog.Close>

						<Dialog.Close asChild>
							<button
								type="button"
								onClick={handleBatchDelete}
								className="inline-flex items-center gap-1 rounded-md border border-dm-red px-3 py-1.5 font-semibold text-dm-red transition-colors hover:border-red hover:bg-red hover:text-dm-white"
							>
								<Trash />
								Confirm Deletion
							</button>
						</Dialog.Close>
					</Dialog.Footer>
				</Dialog.Content>
			</Dialog.Root>
		</Popover.Root>
	);
};
